import { useState } from "react";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const LEAD_STAGE_INFO = {
  New: "Fresh enquiry just logged, no contact yet. Reach out within 24–48 hours.",
  Contacted: "First conversation done — understanding the project and its acoustic requirement.",
  Nurturing: "Genuine project but far from purchase (design not frozen / budget pending). Stay in periodic touch.",
  Qualified: "Real project with budget, decision-maker and timeline confirmed. Convert to an Opportunity once a quotation has been shared with the client.",
  "Lead Lost": "Genuine enquiry that went cold or chose another route before becoming an opportunity.",
};

export const OPP_STAGE_INFO = {
  "Quotation / BOQ": "The opportunity starts here — a quotation / BOQ has been shared (or is being prepared) for the client's review.",
  "Negotiation & Follow-up": "Commercial discussions on price, terms and delivery; closing follow-ups.",
  "Order Won": "PO received and uploaded — reason captured. Booking value counts in BI.",
  "Order Booked": "Won order entered/booked in the ERP (sales order created). Move here from Order Won once the SO is raised.",
  "Order Fulfilled": "All dispatches done and the order is fully serviced/complete. Move here from Order Won / Order Booked once delivery is finished.",
  "Order Lost": "Client decided against us — competitor, dropped or shelved. Lost reason required.",
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function HoverPopover({ testid, label, content }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={label}
          onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
          className="inline-flex items-center justify-center align-middle text-slate-400 hover:text-slate-600 transition-colors shrink-0"
          data-testid={testid}>
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={6}
        onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-80 max-h-80 overflow-y-auto p-3 z-[70]">
        {content}
      </PopoverContent>
    </Popover>
  );
}

/** Hoverable/tappable "i" explaining a single stage, or the full stage guide when no stage is given. */
export function StageInfo({ kind = "lead", stage }) {
  const map = kind === "lead" ? LEAD_STAGE_INFO : OPP_STAGE_INFO;
  const entries = stage ? (map[stage] ? [[stage, map[stage]]] : []) : Object.entries(map);
  if (!entries.length) return null;
  return (
    <HoverPopover
      testid={`stage-info-${kind}${stage ? `-${slug(stage)}` : ""}`}
      label={stage ? `What does ${stage} mean?` : "Stage guide"}
      content={
        <div className="space-y-2 text-left">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            {kind === "lead" ? "Lead status guide" : "Opportunity stage guide"}
          </p>
          {entries.map(([s, txt]) => (
            <div key={s}>
              <p className="text-xs font-semibold text-slate-800">{s}</p>
              <p className="text-xs text-slate-500 leading-snug">{txt}</p>
            </div>
          ))}
        </div>
      }
    />
  );
}

/** Generic hoverable "i" with a short explanation. */
export function InfoHint({ text, testid = "info-hint" }) {
  return <HoverPopover testid={testid} label="More info" content={<p className="text-xs text-slate-600 leading-snug text-left">{text}</p>} />;
}
